import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Modal, Result, Spin, Typography, Upload } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { getContract, uploadContract } from "./api";
import "./vendorContracts.css";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp";
const MAX_FILES = 10;
const POLL_MS = 2500;
// Photos from a phone are 3–10 MB and 4000+ px wide; the model reads text well at ~2000 px,
// and the API takes at most 5 MB per image.
const MAX_IMAGE_EDGE = 2000;

/** Scales a photo down to MAX_IMAGE_EDGE as JPEG. Returns the original if it cannot. */
const shrinkImage = file =>
  new Promise(resolve => {
    if (!file.type.startsWith("image/")) return resolve(file);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(img.width, img.height));
      if (scale === 1 && file.size < 3 * 1024 * 1024) return resolve(file);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        blob =>
          resolve(
            blob
              ? new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" })
              : file
          ),
        "image/jpeg",
        0.85
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });

const UploadContractModal = ({ open, onClose, onRead }) => {
  const { t, i18n } = useTranslation("dashboard");
  const [fileList, setFileList] = useState([]);
  const [phase, setPhase] = useState("choose"); // choose | uploading | reading | failed
  const [error, setError] = useState("");
  const pollTimer = useRef(null);

  const reset = () => {
    clearTimeout(pollTimer.current);
    setFileList([]);
    setPhase("choose");
    setError("");
  };

  useEffect(() => {
    if (open) reset();
    return () => clearTimeout(pollTimer.current);
  }, [open]);

  const poll = id => {
    pollTimer.current = setTimeout(async () => {
      try {
        const res = await getContract(id);
        const { extraction } = res.data.data;
        if (extraction.status === "processing") return poll(id);
        if (extraction.status === "failed") {
          setError(extraction.error);
          setPhase("failed");
          onRead(res.data.data, false);
          return;
        }
        onRead(res.data.data, true);
      } catch (err) {
        generalErrorAlert(err);
        setPhase("failed");
      }
    }, POLL_MS);
  };

  const handleSubmit = async () => {
    setPhase("uploading");
    try {
      const files = await Promise.all(fileList.map(item => shrinkImage(item.originFileObj)));
      const res = await uploadContract(files, (i18n.language || "de").slice(0, 2));
      setPhase("reading");
      poll(res.data.data._id);
    } catch (err) {
      generalErrorAlert(err);
      setPhase("choose");
    }
  };

  const busy = phase === "uploading" || phase === "reading";

  return (
    <Modal
      open={open}
      title={t("Upload contract")}
      className="vc-upload-modal"
      onCancel={onClose}
      maskClosable={!busy}
      destroyOnClose
      footer={
        phase === "choose"
          ? [
              <Button key="cancel" onClick={onClose}>
                {t("Cancel")}
              </Button>,
              <Button key="read" type="primary" disabled={!fileList.length} onClick={handleSubmit}>
                {t("Read contract")}
              </Button>
            ]
          : phase === "failed"
            ? [
                <Button key="close" onClick={onClose}>
                  {t("Close")}
                </Button>
              ]
            : null
      }
    >
      {phase === "choose" && (
        <>
          <Upload.Dragger
            multiple
            accept={ACCEPT}
            fileList={fileList}
            beforeUpload={() => false}
            onChange={({ fileList: next }) => setFileList(next.slice(0, MAX_FILES))}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t("Drop the contract here or click to choose")}</p>
            <p className="ant-upload-hint">
              {t(
                "A PDF, or photos of the pages (JPG, PNG). Several files are read as one contract."
              )}
            </p>
          </Upload.Dragger>
          <Typography.Paragraph type="secondary" className="vc-upload-note">
            {t(
              "The assistant reads the price, the term and the notice period. The document is sent to Google (Gemini) for this and is filed in your Directory under Verträge. Please check the result before saving."
            )}
          </Typography.Paragraph>
        </>
      )}
      {busy && (
        <div className="vc-upload-progress">
          <Spin size="large" />
          <Typography.Title level={5}>
            {phase === "uploading" ? t("Uploading…") : t("Reading the contract…")}
          </Typography.Title>
          <Typography.Text type="secondary">
            {t(
              "This usually takes 20 to 60 seconds. You can close this window; the contract appears in the list when it is ready."
            )}
          </Typography.Text>
        </div>
      )}
      {phase === "failed" && (
        <Result
          status="warning"
          title={t("The contract could not be read")}
          subTitle={
            <>
              {error && <Alert type="error" message={error} className="mb-2" />}
              {t(
                "The document is saved. You can fill in the details yourself or try again from the list."
              )}
            </>
          }
        />
      )}
    </Modal>
  );
};

export default UploadContractModal;
